import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api, fileUrl, fmtIQD } from "../lib/api";
import { useCart } from "../lib/cart";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductImage from "../components/ProductImage";
import { Minus, Plus, ShoppingCart, Check } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [p, setP] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [variantId, setVariantId] = useState("");
  const [flavor, setFlavor] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    (async () => {
      const r = await api.get(`/products/${id}`);
      setP(r.data);
      const firstAvail = r.data.variants?.find((v) => v.stock > 0 && v.is_available !== false);
      setVariantId(firstAvail?.id || r.data.variants?.[0]?.id || "");
      setFlavor(r.data.flavors?.[0] || "");
      setColor(r.data.colors?.[0] || "");
      setImgIdx(0);
    })();
  }, [id]);

  if (!p) return (
    <div className="App"><Header /><div className="pt-32 pg-container">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="aspect-square pg-skeleton rounded-lg" />
        <div className="space-y-3"><div className="h-8 w-2/3 pg-skeleton rounded" /><div className="h-4 w-full pg-skeleton rounded" /><div className="h-4 w-full pg-skeleton rounded" /></div>
      </div></div></div>
  );

  const gallery = p.gallery?.length ? p.gallery : [p.cover_image];
  const selectedVar = p.variants?.find((v) => v.id === variantId);
  const orderingEnabled = p.ordering_enabled !== false;
  const price = selectedVar ? selectedVar.price : p.final_price;
  const stock = selectedVar ? selectedVar.stock : 99;
  const canAdd = orderingEnabled && stock > 0 && (selectedVar?.is_available !== false);

  const handleAdd = () => {
    if (p.ordering_enabled === false) {
      toast.error("هذا المنتج غير متاح للطلب حالياً");
      return;
    }
    const ok = add(p, selectedVar || null, qty);
    if (!ok) {
      toast.error("يرجى تسجيل الدخول أو إنشاء حساب لإضافة المنتجات إلى السلة");
      const returnTo = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?returnTo=${returnTo}`);
      return;
    }
    toast.success("تمت الإضافة إلى السلة");
  };

  return (
    <div className="App">
      <Header />
      <div className="pg-page-top pb-16">
        <div className="pg-container">
          <div className="text-xs text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#EF1B17]">الرئيسية</Link>
            <span className="mx-2">/</span>
            <Link to={`/${p.product_type === "supplement" ? "supplements" : p.product_type === "clothing" ? "sportswear" : "accessories"}`} className="hover:text-[#EF1B17]">
              {p.product_type === "supplement" ? "المكملات" : p.product_type === "clothing" ? "الملابس" : "الإكسسوارات"}
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-14">
            {/* Gallery */}
            <div>
              <div className="border border-[#3A3D43] rounded-lg overflow-hidden">
                <ProductImage data-testid="product-image" src={gallery[imgIdx]} alt={p.name} size="detail" priority className="bg-[#191A1D]" />
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-2 mt-3">
                  {gallery.map((g, i) => (
                    <button key={i} onClick={() => setImgIdx(i)}
                      className={`w-20 h-20 rounded border overflow-hidden ${i === imgIdx ? "border-[#EF1B17]" : "border-[#3A3D43]"}`}>
                      <ProductImage src={g} alt="" size="thumb" className="w-full h-full" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              {p.agency_name && <div className="text-xs font-bold uppercase tracking-widest text-[#EF1B17] mb-2">{p.agency_name}</div>}
              <h1 data-testid="product-title" className="text-3xl md:text-4xl font-black mb-3">{p.name}</h1>

              <div className="flex items-baseline gap-3 mb-6">
                <span data-testid="product-price" className="text-3xl font-black">{fmtIQD(price)}</span>
                {p.previous_price && p.previous_price > price && (
                  <>
                    <span className="text-lg text-gray-500 line-through">{fmtIQD(p.previous_price)}</span>
                    <span className="bg-[#EF1B17] text-white text-xs font-bold px-2 py-0.5 rounded">-{p.discount_percent}%</span>
                  </>
                )}
              </div>

              {p.description && <p className="text-gray-300 leading-relaxed mb-6">{p.description}</p>}

              {/* Variants */}
              {p.variants?.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">
                    {p.product_type === "clothing" ? "المقاس" : p.product_type === "supplement" ? "الوزن / الحجم" : "النوع"}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {p.variants.map((v) => {
                      const disabled = v.stock <= 0 || v.is_available === false;
                      const active = v.id === variantId;
                      return (
                        <button
                          key={v.id}
                          data-testid={`variant-${v.id}`}
                          disabled={disabled}
                          onClick={() => setVariantId(v.id)}
                          className={`px-4 py-2 rounded-md text-sm font-bold border transition-colors ${
                            active ? "bg-[#EF1B17] border-[#EF1B17] text-white" :
                            disabled ? "border-[#3A3D43] text-gray-600 line-through cursor-not-allowed" :
                            "border-[#3A3D43] hover:border-[#F4F4F2]"
                          }`}
                        >
                          {v.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Flavors */}
              {p.flavors?.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">النكهة</div>
                  <div className="flex flex-wrap gap-2">
                    {p.flavors.map((f) => (
                      <button key={f} onClick={() => setFlavor(f)}
                        className={`px-4 py-2 rounded-md text-sm font-bold border ${f === flavor ? "bg-[#24262B] border-[#EF1B17] text-[#EF1B17]" : "border-[#3A3D43] hover:border-[#F4F4F2]"}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Colors */}
              {p.colors?.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">اللون</div>
                  <div className="flex flex-wrap gap-2">
                    {p.colors.map((c) => (
                      <button key={c} onClick={() => setColor(c)}
                        className={`px-4 py-2 rounded-md text-sm font-bold border ${c === color ? "bg-[#24262B] border-[#EF1B17] text-[#EF1B17]" : "border-[#3A3D43] hover:border-[#F4F4F2]"}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Availability status */}
              {!orderingEnabled && (
                <div className="mb-3 inline-flex items-center gap-2 bg-gray-800/50 border border-gray-500 text-gray-200 rounded-md px-3 py-2 text-sm font-bold">
                  غير متوفر حالياً
                </div>
              )}

              {/* Stock */}
              <div className={`inline-flex items-center gap-2 text-sm mb-4 ${stock > 0 ? "text-green-400" : "text-red-400"}`}>
                <Check size={14} /> {stock > 0 ? `متوفر (${stock} قطعة)` : "غير متوفر حالياً"}
              </div>

              {/* Qty + Add */}
              <div className="flex items-stretch gap-3 mt-4">
                <div className="flex items-center bg-[#24262B] border border-[#3A3D43] rounded-md">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3 hover:text-[#EF1B17]"><Minus size={16} /></button>
                  <span data-testid="qty-value" className="w-10 text-center font-bold">{qty}</span>
                  <button onClick={() => setQty(Math.min(stock, qty + 1))} className="px-3 py-3 hover:text-[#EF1B17]"><Plus size={16} /></button>
                </div>
                <button
                  data-testid="add-to-cart-btn"
                  disabled={!canAdd}
                  onClick={handleAdd}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#EF1B17] hover:bg-[#FF3B30] disabled:bg-[#3A3D43] disabled:cursor-not-allowed text-white font-bold py-3 rounded-md pg-lift"
                >
                  <ShoppingCart size={18} /> إضافة إلى السلة
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
